import * as XLSX from 'xlsx';
import type { NedarimTransactionDtoIn, NedarimTransactionFundAllocationErrorDtoIn } from '@/models/in/NedarimTransactionDtoIn';
import type { NedarimTransactionFundAllocationDtoOut } from '@/models/out/NedarimTransactionFundAllocationDtoOut';

export const TRANSACTIONS_WITHOUT_FUND_URL = 'nedarim-transactions/without-fund';
export const ALLOCATE_FUNDS_URL = 'nedarim-transactions/allocate-funds';
export const FUNDS_LOOKUP_URL = 'nedarim-transactions/funds';
export const AUTO_CLASSIFIED_URL = 'nedarim-transactions/auto-classified';
export const CLASSIFY_EXISTING_URL = 'nedarim-transactions/classify-existing';
export const DECODE_ENTRIES_URL = 'nedarim-decode-entries';

const COLUMNS_PREFIX = 'nedarimTransactionsFundAllocation.columns.';

/**
 * The office round-trips this spreadsheet, so the file the new screen writes and the file it
 * reads back have to stay byte-for-byte the ones the old screen used: the same header labels in
 * the same order, the same sheet and file names, and the same right-to-left workbook view.
 */
const TRANSACTIONS_SHEET_NAME = 'תנועות ללא פרויקט';
const ERRORS_SHEET_NAME = 'שגיאות בשיוך';
const TRANSACTIONS_FILE_NAME = 'תנועות_ללא_פרויקט.xlsx';
const ERRORS_FILE_NAME = 'שגיאות_שיוך_פרויקטים.xlsx';

/** Header labels a file saved before the screen was translated still carries. */
const FALLBACK_FUND_ID_HEADER = 'מזהה פרויקט';
const LEGACY_FUND_ID_HEADER = 'מזהה קרן';
const FALLBACK_TRANSACTION_ID_HEADER = 'מזהה תנועה';

const DEFAULT_COLUMN_WIDTH = 15;
const WIDE_COLUMNS: Record<string, number> = {
  errorMessage: 40,
  transactionTime: 20,
  clientName: 25,
  adresse: 25,
  mail: 25,
  comments: 30,
};

/** The timestamp columns, which go into the sheet as readable local text rather than ISO strings. */
const DATE_PROPERTIES = ['transactionTime', 'insertDate'];

export type TranslateFn = (key: string) => string;

/** The tables show the transaction moment as readable local text, not the raw ISO string. */
export const formatTransactionTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** i18next hands back the key itself when a label is missing; the raw property name reads better. */
const columnLabel = (t: TranslateFn, property: string): string => {
  const key = `${COLUMNS_PREFIX}${property}`;
  const label = t(key);
  return !label || label === key ? property : label;
};

type ExcelRow = Record<string, unknown>;

const cellValue = (property: string, value: unknown): string => {
  if (DATE_PROPERTIES.includes(property)) {
    return value ? new Date(value as string).toLocaleString('he-IL') : '';
  }
  return value != null ? String(value) : '';
};

/**
 * Writes the spreadsheet for a set of transactions. The columns are taken from the first row, so
 * an error report - which carries the same transaction plus the reason it failed - only differs
 * in the error column pushed in front of them.
 */
function generateExcelFile(rows: ExcelRow[], t: TranslateFn, isErrorReport: boolean): void {
  const properties = Object.keys(rows[0]);

  const data = rows.map((row) => {
    const sheetRow: Record<string, string> = {};

    if (isErrorReport) {
      sheetRow[columnLabel(t, 'errorMessage')] = (row.errorMessage as string) ?? '';
    }

    properties.forEach((property) => {
      sheetRow[columnLabel(t, property)] = cellValue(property, row[property]);
    });

    return sheetRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  workbook.Workbook = { Sheets: [], Views: [{ RTL: true }] };

  const widthByLabel = Object.fromEntries(Object.entries(WIDE_COLUMNS).map(([property, width]) => [columnLabel(t, property), width]));
  worksheet['!cols'] = Object.keys(data[0]).map((header) => ({
    wch: widthByLabel[header] ?? DEFAULT_COLUMN_WIDTH,
  }));

  XLSX.utils.book_append_sheet(workbook, worksheet, isErrorReport ? ERRORS_SHEET_NAME : TRANSACTIONS_SHEET_NAME);
  XLSX.writeFile(workbook, isErrorReport ? ERRORS_FILE_NAME : TRANSACTIONS_FILE_NAME, { bookSST: false });
}

export function downloadTransactionsWithoutFundFile(transactions: NedarimTransactionDtoIn[], t: TranslateFn): void {
  generateExcelFile(transactions as unknown as ExcelRow[], t, false);
}

export function downloadAllocationErrorsFile(errors: NedarimTransactionFundAllocationErrorDtoIn[], t: TranslateFn): void {
  const rows: ExcelRow[] = errors.map((error) =>
    error.transaction
      ? { ...error.transaction, fundId: error.fundId, errorMessage: error.errorMessage }
      : { id: error.transactionId, fundId: error.fundId, errorMessage: error.errorMessage }
  );

  generateExcelFile(rows, t, true);
}

export interface InvalidAllocationEntry {
  rowNumber: number;
  fundId: string;
}

export interface ParsedAllocations {
  allocations: NedarimTransactionFundAllocationDtoOut[];
  invalidEntries: InvalidAllocationEntry[];
}

const readCell = (row: Record<string, unknown>, header: string, ...fallbackHeaders: string[]): unknown => {
  if (row[header] !== undefined) return row[header];
  for (const fallbackHeader of fallbackHeaders) {
    if (row[fallbackHeader] !== undefined) return row[fallbackHeader];
  }
  return undefined;
};

/**
 * Reads back the filled-in spreadsheet. A row with no transaction id, and a row whose fund is
 * still blank, are simply passed over - the office uploads the whole file, having filled in only
 * the transactions it could place.
 */
export function parseAllocationsFile(file: File, t: TranslateFn): Promise<ParsedAllocations> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(new Uint8Array(event.target?.result as ArrayBuffer), { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet);

        const fundIdHeader = columnLabel(t, 'fundId');
        const transactionIdHeader = columnLabel(t, 'id');

        const allocations: NedarimTransactionFundAllocationDtoOut[] = [];
        const invalidEntries: InvalidAllocationEntry[] = [];

        rows.forEach((row, index) => {
          // The header takes the first line, and the index is zero based.
          const rowNumber = index + 2;

          const transactionId = readCell(row, transactionIdHeader, FALLBACK_TRANSACTION_ID_HEADER);
          if (!transactionId) return;

          const fundId = readCell(row, fundIdHeader, FALLBACK_FUND_ID_HEADER, LEGACY_FUND_ID_HEADER);
          if (fundId === undefined || fundId === null || fundId === '') return;

          const fundIdText = String(fundId).trim();
          const fundIdNumber = Number(fundIdText);

          if (isNaN(fundIdNumber) || !Number.isInteger(fundIdNumber) || fundIdNumber <= 0) {
            invalidEntries.push({ rowNumber, fundId: fundIdText });
          } else {
            allocations.push({ transactionId: String(transactionId), fundId: fundIdNumber });
          }
        });

        resolve({ allocations, invalidEntries });
      } catch {
        reject(new Error('parse'));
      }
    };

    reader.onerror = () => reject(new Error('parse'));
    reader.readAsArrayBuffer(file);
  });
}
